import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  input,
  OnDestroy,
  signal,
  ViewChild,
  inject,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  DxBulletModule,
  DxButtonModule,
  DxCheckBoxModule,
  DxDateBoxModule,
  DxDateRangeBoxModule,
  DxDropDownBoxModule,
  DxDropDownButtonModule,
  DxLoadIndicatorModule,
  DxPopupModule,
  DxRadioGroupModule,
  DxSelectBoxModule,
  DxTemplateModule,
  DxTextBoxModule,
  DxToastModule,
  DxTreeViewComponent,
  DxTreeViewModule,
} from 'devextreme-angular';
import { DxDataGridComponent, DxDataGridModule } from 'devextreme-angular/ui/data-grid';
import { DxoSortingModule } from 'devextreme-angular/ui/nested';
import { RowDblClickEvent, RowExpandedEvent, RowPreparedEvent, SelectionChangedEvent } from 'devextreme/ui/data_grid';
import notify from 'devextreme/ui/notify';
import { firstValueFrom, of, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap, takeUntil, tap } from 'rxjs/operators';

import { LoaderComponent } from '../../loader/loader.component';
import { ContextService } from '../../services/context.service';
import { PlanningHelperService } from '../../services/planning-helper.service';
import { UtilsService } from '../../services/utils.service';
import { IconComponent } from '../../shared/components/icon/icon.component';
import {
  AssignmentDto,
  AssignmentService,
  AssignmentSubtableDataDto,
  AssignmentType,
  GetAssignmentDto,
  GetAssignmentsDto,
} from '../../shared/generated/backend';
import { SharedModule } from '../../shared/shared.module';
import { AssignmentInfoBadgesComponent } from '../../shared/components/assignment-info-badges/assignment-info-badges.component';
import { AssignmentHelper } from '../../shared/util/assignment-helper';
import {
  AssignmentExtendedDto,
  isPlannedStation,
  toAssignmentExtendedDto,
} from '../../shared/util/assignment-extended';

interface GridKey {
  AssignmentNo: number;
  Type: AssignmentType;
}

type SearchState = {
  query: string;
  filterByDate: boolean;
  date: Date | null;
};

@Component({
  selector: 'app-assignment-list-data-grid',
  imports: [
    CommonModule,
    SharedModule,
    DxDataGridModule,
    DxTemplateModule,
    DxBulletModule,
    DxDateRangeBoxModule,
    DxPopupModule,
    DxSelectBoxModule,
    DxButtonModule,
    DxDateBoxModule,
    DxCheckBoxModule,
    DxRadioGroupModule,
    DxToastModule,
    LoaderComponent,
    DxLoadIndicatorModule,
    DxoSortingModule,
    DxDropDownButtonModule,
    DxTreeViewModule,
    DxDropDownBoxModule,
    DxTextBoxModule,
    IconComponent,
    AssignmentInfoBadgesComponent,
  ],
  templateUrl: './assignment-list-data-grid.component.html',
  styleUrls: ['./assignment-list-data-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssignmentListDataGridComponent implements OnDestroy {
  /**
   * Optional external data source (e.g. Prime Logistic stations).
   * When provided, the grid renders this data directly and the search
   * toolbar / search pipeline are disabled ("stations mode").
   */
  readonly stations = input<AssignmentDto[] | null>(null);

  /**
   * The Prime Logistic main assignment the {@link stations} belong to. Its Volume/Weight
   * is the complete load every station has to have planned; a station's remaining
   * (Diff.) amount is that load minus the station's own tracked planned amount.
   */
  readonly stationsMain = input<AssignmentDto | null>(null);

  /** True when the grid is fed with external data instead of the search pipeline. */
  readonly isStationsMode = computed(() => this.stations() != null);

  readonly searchAssignments = signal(new Array<AssignmentExtendedDto>());
  private _assignments = new Array<AssignmentExtendedDto>();
  isSearchLoading = signal(false);
  readonly AssignmentType = AssignmentType;

  @ViewChild(DxTreeViewComponent, { static: false }) treeView!: DxTreeViewComponent;
  @ViewChild(DxDataGridComponent, { static: false }) dataGrid!: DxDataGridComponent;

  selectedRows: AssignmentDto[] = [];
  firstSelectedRowDate: Date | null = null;

  private readonly MIN_CHARS = 3;
  private readonly DEBOUNCE_MS = 1000;
  private readonly search$ = new Subject<string>();
  private lastApiKey: string | null = null;
  private lastApiResults: AssignmentExtendedDto[] | null = null;
  private currentQuery = '';
  filterByDate = signal(true);
  now = new Date();
  oneMonthAgo = new Date(this.now.getFullYear(), this.now.getMonth() - 1, this.now.getDate());
  filterDate = signal<Date | null>(this.oneMonthAgo);
  private readonly searchState$ = new Subject<SearchState>();
  private readonly destroy$ = new Subject<void>();

  private contextService = inject(ContextService);
  private assignmentService = inject(AssignmentService);
  private planningHelperService = inject(PlanningHelperService);
  private changeDetectorRef = inject(ChangeDetectorRef);
  public utilService = inject(UtilsService);
  private translationService = inject(TranslateService);

  constructor() {
    this.setupSearchPipeline();

    // In stations mode, feed the grid directly from the input and skip the search pipeline.
    effect(() => {
      const stations = this.stations();
      if (stations == null) return;
      const main = this.stationsMain();

      // A station carries no own load (its Volume/DispoVolume are 0); what it has to have
      // planned is the main's complete load, and what it has planned is tracked separately
      // (StationDispoVolume/-Weight).
      const mapped: AssignmentExtendedDto[] = stations.map(assignment =>
        toAssignmentExtendedDto(assignment, {
          RemainingDispoVol: (main?.Volume ?? 0) - (assignment.StationDispoVolume ?? 0),
          RemainingDispoWeight: (main?.Weight ?? 0) - (assignment.StationDispoWeight ?? 0),
        })
      );

      this._assignments = mapped;
      this.searchAssignments.set([...mapped]);
      this.isSearchLoading.set(false);
      this.changeDetectorRef.markForCheck();
    });
  }

  private setupSearchPipeline(): void {
    this.searchState$
      .pipe(
        // normalize
        map(s => ({
          query: (s.query ?? '').trim(),
          filterByDate: !!s.filterByDate,
          date: s.date ? new Date(s.date) : null,
        })),

        // If date filtering is enabled but no date selected -> do not call backend yet
        tap(s => {
          if (s.query.length < this.MIN_CHARS || (s.filterByDate && !s.date)) {
            this.isSearchLoading.set(false);
            this._assignments = [];
            this.searchAssignments.set([]);
            this.changeDetectorRef.markForCheck();
          }
        }),

        debounceTime(this.DEBOUNCE_MS),

        // distinctUntilChanged for object
        distinctUntilChanged(
          (a, b) =>
            a.query === b.query &&
            a.filterByDate === b.filterByDate &&
            (a.date?.toDateString() ?? '') === (b.date?.toDateString() ?? '')
        ),

        switchMap(state => {
          const { query, filterByDate, date } = state;

          if (query.length < this.MIN_CHARS) return of([] as AssignmentExtendedDto[]);
          if (filterByDate && !date) return of([] as AssignmentExtendedDto[]);

          // build a cache key (query + optional date)
          const dateKey = filterByDate && date ? this.formatDateKey(date) : '';
          const cacheKey = `${query}|${filterByDate ? '1' : '0'}|${dateKey}`;

          // cache hit
          if (this.lastApiKey === cacheKey && this.lastApiResults) {
            return of(this.localFilter(this.lastApiResults, query));
          }

          this.isSearchLoading.set(true);

          // ---- API call with optional date ----
          // You will implement this method in your AssignmentService (see section 3)
          return this.assignmentService.getAssignmentsBySearch(query, filterByDate ? dateKey : undefined).pipe(
            map(ret => {
              const mapped: AssignmentExtendedDto[] = ret.map(assignment =>
                toAssignmentExtendedDto(assignment, {
                  RemainingDispoVol: assignment.Volume - this.dispoVolumeInOverview(assignment),
                  RemainingDispoWeight: assignment.Weight - this.dispoWeightInOverview(assignment),
                })
              );

              this.lastApiKey = cacheKey;
              this.lastApiResults = mapped;

              return this.localFilter(mapped, query);
            }),
            catchError(err => {
              notify(
                { message: err?.statusText ?? this.translationService.instant('ERROR.GENERAL'), shading: false },
                'error',
                3000
              );
              console.error('getAssignments error', err);
              return of([] as AssignmentExtendedDto[]);
            }),
            tap(() => this.isSearchLoading.set(false))
          );
        })
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe(results => {
        this._assignments = results;
        this.showValues();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSearchTextChanged(rawText: string): void {
    this.currentQuery = (rawText ?? '').trim();
    this.emitSearchState();
  }

  onFilterByDateChanged(value: boolean): void {
    this.filterByDate.set(!!value);
    this.emitSearchState();
  }

  onFilterDateChanged(value: Date | string | null): void {
    // DevExtreme can give Date or ISO string depending on config
    const date = value ? new Date(value as string) : this.oneMonthAgo;
    this.filterDate.set(date);
    this.emitSearchState();
  }

  private emitSearchState(): void {
    // Ignore search interactions when the grid is driven by external data.
    if (this.isStationsMode()) return;
    this.searchState$.next({
      query: this.currentQuery,
      filterByDate: this.filterByDate(),
      date: this.filterDate(),
    });
  }

  public isPlanningInProgress(): boolean {
    return this.planningHelperService.planningInProgress;
  }

  /** True when a Prime Logistic station is already assigned to a Trapo project. */
  isPlannedStation(station: AssignmentDto): boolean {
    return isPlannedStation(station);
  }

  private dispoWeightInOverview(item: AssignmentDto): number {
    return item.DispoWeight || 0;
  }

  private dispoVolumeInOverview(item: AssignmentDto): number {
    return item.DispoVolume || 0;
  }

  onRowPrepared(e: RowPreparedEvent): void {
    if (e.rowType === 'data') {
      if (e.data.TransportLabel === 'FarAway') {
        e.rowElement.classList.add('far-away-row');
      }

      if (this.firstSelectedRowDate && e.data.EventDate) {
        const rowDate = new Date(e.data.EventDate);
        rowDate.setHours(0, 0, 0, 0);

        if (rowDate.getTime() === this.firstSelectedRowDate.getTime()) {
          e.rowElement.classList.add('same-date-row');
        }
      }
    }
  }

  async reloadAssignment(index: { AssignmentNo: number; Type: AssignmentType }) {
    try {
      const getAssignmentDto: GetAssignmentDto = {
        AssignmentId: index.AssignmentNo,
        Type: index.Type,
      };

      const request: GetAssignmentsDto = {
        ApplyFilter: true,
        Assignments: [getAssignmentDto],
      };

      const assignments = await firstValueFrom(this.assignmentService.getAssignmentsByIdAndType(request));
      const i = this._assignments.findIndex(a => a.AssignmentNo == index.AssignmentNo && a.Type == index.Type);

      if (i >= 0 && assignments.length > 0 && assignments[0]) {
        this._assignments[i] = assignments[0];
        this._assignments[i].EffectiveVolume = AssignmentHelper.effectiveVolume(this._assignments[i]);
        this._assignments[i].EffectiveWeight = AssignmentHelper.effectiveWeight(this._assignments[i]);
        this._assignments[i].RemainingDispoVol =
          this._assignments[i].Volume - this.dispoVolumeInOverview(this._assignments[i]);
        this._assignments[i].RemainingDispoWeight =
          this._assignments[i].Weight - this.dispoWeightInOverview(this._assignments[i]);

        // Push updated array to the grid
        this.showValues();
      } else {
        console.error('assignment not found', index);
      }
    } catch (err) {
      console.error(err);
    }
  }

  markPlanning(selection: AssignmentDto[]) {
    this.dataGrid.instance.clearSelection();
    selection.forEach(row => {
      const rowIndex = this.dataGrid.instance.getRowIndexByKey({ AssignmentNo: row.AssignmentNo, Type: row.Type });
      const rowElement = this.dataGrid.instance.getRowElement(rowIndex)?.[0];
      if (rowElement) {
        rowElement.classList.add('planning');
      }
    });
  }

  loadAssignment(event: RowDblClickEvent) {
    this.contextService.openContextView(`assignment/${event.data.AssignmentNo}/${event.data.Type!}`);
  }

  async loadAssignmentSubtableData(event: RowExpandedEvent) {
    const key = event.key as GridKey;
    const row = this._assignments.find(t => t.AssignmentNo === key.AssignmentNo && t.Type === key.Type);
    if (!row || row.subtableData !== undefined) return;

    const subtableData = await firstValueFrom(
      this.assignmentService.getAssignmentSubtableData(row.AssignmentNo!, key.Type)
    )
      .then(ret => {
        return {
          ...ret,
          AssignmentNo: row.AssignmentNo,
          Type: key.Type,
          TransportLabel: row.TransportLabel,
        } as AssignmentSubtableDataDto;
      })
      .catch(err => {
        console.error('Error loading subtable data', err);
        notify({ message: this.translationService.instant('ERROR.GENERAL'), shading: false }, 'error', 3000);
        if (this.dataGrid?.instance) this.dataGrid.instance.collapseAll(-1);
      });

    if (subtableData === undefined) return;
    row.subtableData = subtableData;
    this.showValues();
  }

  updateSelectionOrder(e: SelectionChangedEvent) {
    this.selectedRows = e.selectedRowsData;

    if (this.selectedRows.length > 0 && this.selectedRows[0].EventDate) {
      this.firstSelectedRowDate = new Date(this.selectedRows[0].EventDate);
      this.firstSelectedRowDate.setHours(0, 0, 0, 0);
    } else {
      this.firstSelectedRowDate = null;
    }

    this.updateRowHighlighting();
  }

  updateRowHighlighting() {
    if (!this.dataGrid?.instance) return;

    const visibleRows = this.dataGrid.instance.getVisibleRows();

    visibleRows.forEach(row => {
      if (row.rowType === 'data') {
        const rowElement = this.dataGrid.instance.getRowElement(row.rowIndex)?.[0];
        if (rowElement) {
          rowElement.classList.remove('same-date-row');

          if (this.firstSelectedRowDate && row.data.EventDate) {
            const rowDate = new Date(row.data.EventDate);
            rowDate.setHours(0, 0, 0, 0);

            if (rowDate.getTime() === this.firstSelectedRowDate.getTime()) {
              rowElement.classList.add('same-date-row');
            }
          }
        }
      }
    });
  }

  private localFilter(items: AssignmentExtendedDto[], query: string): AssignmentExtendedDto[] {
    const q = query.trim().toLowerCase();
    if (q.length < this.MIN_CHARS) return [];

    // keep your current rule: match in AssignmentNo
    return items.filter(a =>
      String(a.AssignmentNo ?? '')
        .toLowerCase()
        .includes(q)
    );
  }

  private showValues(): void {
    const data = [...this._assignments];
    this.searchAssignments.set(data);
    this.isSearchLoading.set(false);
    this.changeDetectorRef.markForCheck();
  }

  private formatDateKey(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
